// src/App.js
import React from 'react';
import { Suspense, lazy } from "react";
// Import `useLocation` to read the current URL
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import Contact from './pages/Contact';
import LiveKitAssistant from './components/LiveKitAgent/LiveKitAssistant';
import './App.css';

// Lazy-loaded components
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./components/Services/service"));
const BookCallNowButton = lazy(() =>
  import("./components/BookCall/AnimatedBookCallButton.tsx")
);
const Solutions = lazy(() => import('./components/Domain/WhatWeOffer'));
// const Testimonials = lazy(() => import('./components/Testimonials/Testimonial'));

// A new component to contain the logic that needs access to the router's context
function AppContent() {
  const location = useLocation();
  const showFloatingButton = location.pathname !== "/";
  // If navigation came from header nav/logo which forced a full page load,
  // scroll to top on first render and clean any flags/query params.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromNav = params.get('fromNav') === '1' || sessionStorage.getItem('fromNav') === '1';
      const fromLogo = params.get('fromLogo') === '1' || sessionStorage.getItem('fromLogo') === '1';

      if (fromNav || fromLogo) {
        // Prevent browser auto-restoration while we perform our manual scroll.
        let prevScrollRestoration;
        try {
          if ('scrollRestoration' in window.history) {
            prevScrollRestoration = window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
          }
        } catch (e) {
          // ignore
        }

        // Use a valid behavior value and a small timeout to ensure this runs
        // after layout and any browser restoration attempts.
        setTimeout(() => {
          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          } catch (e) {
            window.scrollTo(0, 0);
          }

          // restore scrollRestoration if we changed it
          try {
            if (prevScrollRestoration !== undefined && 'scrollRestoration' in window.history) {
              window.history.scrollRestoration = prevScrollRestoration;
            }
          } catch (e) { /* ignore */ }
        }, 20);

        // clear session flags
        try { sessionStorage.removeItem('fromNav'); sessionStorage.removeItem('fromLogo'); } catch (err) { }

        // remove query params without reloading
        if (params.get('fromNav') === '1' || params.get('fromLogo') === '1') {
          params.delete('fromNav');
          params.delete('fromLogo');
          const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
    } catch (err) {
      // ignore
    }
  }, []);

  

  return (
    <div className="app">
      <Header />
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/services" element={<Services />} />
        </Routes>
      </AnimatePresence>

      {/* Floating over everything, clickable, hidden on Hero */}
      {showFloatingButton && <BookCallNowButton variant="floating" />}

      <LiveKitAssistant />

      <Footer />
    </div>
  );
}
function App() {
  return (
    <Suspense fallback={<div className="w-full h-screen flex justify-center items-center">Loading...</div>}>
      <Router>
        {/* The AppContent component is wrapped by Router, so it can use 'useLocation' */}
        <AppContent />
      </Router>
    </Suspense>
  );
}

export default App;